/**
 * Main component for the game bar. This component fetches the game data from the API and displays it in a horizontal scrollable bar as GameBoxes and DateBoxes.
 */
import React, { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ThreeDots } from 'react-loader-spinner';
import DateBox from './DateBox';
import GameBox from './GameBox';

export default function GameBar() {
  // useRef hook to reference the scrollable container
  const scrollContainerRef = useRef(null);

  const { isLoading, error, data } = useQuery({
    staleTime: 60000,
    queryKey: ["gameBar"],
    queryFn: () =>
      fetch(`${process.env.REACT_APP_BUIHA_API_URL}/game/grouped/`).then((res) =>
        res.json(),
      ),
  });

  // Handle scrolling when arrow buttons are clicked
  const scrollLeft = () => {
    scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
  };

  if (isLoading) return (
    <div className="flex border-y-2 border-light-3 dark:border-dark-3 w-full h-[90px] xxs:h-24 md:h-28 overflow-hidden justify-center items-center">
      <ThreeDots visible={true} height="80" width="80" color="#8b8680" radius="5" ariaLabel="three-dots-loading" wrapperStyle={{}} wrapperClass="" />
    </div>
  );

  if (error) return 'An error has occurred: ' + error.message;

  return (
    <div className="relative flex border-y-2 border-light-3 dark:border-dark-3 w-full h-[90px] xxs:h-24 md:h-28 items-center">
      
      {/* Left Arrow Button */}
      <button 
        className="absolute left-0 z-10 h-full px-1 xxs:px-2 bg-light-4 dark:bg-dark-1 hover:bg-light-3 dark:hover:bg-dark-6 border-r-2 border-light-3 dark:border-dark-3 ease-in-out duration-200"
        onClick={scrollLeft}
      >
        <img src="/assets/icons/left-arrow.png" alt="left-arrow" className="w-4 h-4" 
        style={
          window.matchMedia &&
          window.matchMedia("(prefers-color-scheme: dark)").matches
            ? { filter: "brightness(0) invert(1)" }
            : {}
        } />
      </button>

      {/* Scrollable Container */}
      <div 
        ref={scrollContainerRef} 
        className="mx-[22px] xxs:mx-[30px] flex overflow-x-auto h-full w-full scrollbar-hide"
      >
        {/* For each day */}
        {Object.keys(data).map((date, index) => (
          <React.Fragment key={index}>
            {/* Date box */}
            <DateBox 
              month={date.split(" ")[1]} 
              day={date.split(" ")[0]} 
            />

            {/* For each game */}
            {data[date].map((game, gameIndex) => (
              <GameBox key={gameIndex} {...game} />
            ))}
          </React.Fragment>
        ))}
      </div>

      {/* Right Arrow Button */}
      <button 
        className="absolute right-0 z-10 h-full px-1 xxs:px-2 bg-light-4 dark:bg-dark-1 hover:bg-light-3 dark:hover:bg-dark-6 border-l-2 border-light-3 dark:border-dark-3 ease-in-out duration-200"
        onClick={scrollRight}
      >
       <img src="/assets/icons/right-arrow.png" alt="right-arrow" className="w-4 h-4" 
        style={
          window.matchMedia &&
          window.matchMedia("(prefers-color-scheme: dark)").matches
            ? { filter: "brightness(0) invert(1)" }
            : {}
        } />
      </button>
    </div>
  );
}